import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { Box, Paper, Typography } from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import moment from "moment";
import { calcDistanceToPoints } from "Components/Map/utils";

const METERS_TO_MILES = 1 / 1609.344;

const formatDuration = (ms, messages) => {
  const d = moment.duration(ms);
  const hrs = Math.floor(d.asHours());
  const mins = d.minutes();
  return `${hrs} ${messages["realtimemaps_vehicletrails_trip_hrs"]}, ${mins} ${messages["realtimemaps_vehicletrails_trip_mins"]}`;
};

const distanceBetween = (a, b) => {
  if (
    a?.latitude == null ||
    a?.longitude == null ||
    b?.latitude == null ||
    b?.longitude == null
  )
    return 0;
  return calcDistanceToPoints(
    a.latitude,
    a.longitude,
    b.latitude,
    b.longitude
  );
};

export const VehicleTripsResume = ({ unit, vehicleTrips, onTripClick }) => {
  const { messages } = useIntl();

  const aggregates = useMemo(() => {
    const items = vehicleTrips?.data ?? [];

    const validTrips = items.filter(
      (t) => t?.start?.unitTime && t?.end?.unitTime
    );
    if (!validTrips.length) return null;

    const sorted = [...validTrips].sort(
      (a, b) =>
        new Date(a.start.unitTime).getTime() -
        new Date(b.start.unitTime).getTime()
    );

    let totalDurationMs = 0;
    let totalDistanceMeters = 0;
    let stopsDurationMs = 0;

    for (const trip of sorted) {
      const startM = moment(trip.start.unitTime);
      const endM = moment(trip.end.unitTime);
      totalDurationMs += endM.diff(startM);
      totalDistanceMeters += distanceBetween(trip.start, trip.end);
    }

    for (let i = 0; i < sorted.length - 1; i++) {
      const endCurrent = moment(sorted[i].end.unitTime);
      const endNext = moment(sorted[i + 1].end.unitTime);
      stopsDurationMs += endNext.diff(endCurrent);
    }

    const deviceId =
      sorted[0]?.start?.deviceId ?? sorted[0]?.end?.deviceId ?? "—";

    return {
      deviceId,
      totalDurationMs,
      totalDistanceMiles: totalDistanceMeters * METERS_TO_MILES,
      stopsCount: sorted.length,
      stopsDurationMs,
    };
  }, [vehicleTrips]);

  if (!aggregates) return null;

  const totalDurationFormatted = formatDuration(
    aggregates.totalDurationMs,
    messages
  );
  const totalDistanceMiles = aggregates.totalDistanceMiles.toFixed(1);
  const stopsDurationFormatted = formatDuration(
    aggregates.stopsDurationMs,
    messages
  );

  const iconStyle = { color: "rgba(0, 0, 0, 0.54)", fontSize: 20, mr: 1 };
  const rowSx = {
    display: "flex",
    alignItems: "center",
    py: 0.75,
  };

  return (
    <Paper
      data-testid="vehicle-trips-resume"
      elevation={0}
      sx={{
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "background.paper",
        mt: 2,
      }}
    >
      <Box sx={rowSx}>
        <LocalShippingIcon sx={iconStyle} />
        <Typography
          component="button"
          variant="body2"
          onClick={() => onTripClick?.(aggregates.deviceId)}
          sx={{
            border: 0,
            background: "none",
            cursor: onTripClick ? "pointer" : "default",
            p: 0,
            font: "inherit",
          }}
        >
          {unit?.label}
        </Typography>
      </Box>
      <Box sx={rowSx}>
        <PlayArrowIcon sx={iconStyle} />
        <Typography variant="body2" color="text.secondary">
          {totalDurationFormatted} • {totalDistanceMiles}{" "}
          {messages["realtimemaps_vehicletrails_trip_miles"]}
        </Typography>
      </Box>
      <Box sx={rowSx}>
        <StopIcon sx={iconStyle} />
        <Typography variant="body2" color="text.secondary">
          {stopsDurationFormatted} • {aggregates.stopsCount}{" "}
          {messages["realtimemaps_vehicletrails_trip_stops"]}
        </Typography>
      </Box>
      <Box sx={rowSx}>
        <CheckCircleOutlineIcon sx={iconStyle} />
        <Typography variant="body2" color="text.secondary">
          0 {messages["realtimemaps_vehicletrails_trip_io_events"]}
        </Typography>
      </Box>
    </Paper>
  );
};
